import React, { useRef } from "react";
import clsx from "clsx";

import Modal from "../../../atoms/Modal";
import {
  BULK_CREATE_GROUPS_SUCCESS_MESSAGE,
  Context,
  bulkCreateGroups,
  clearSuccessMessage,
  deleteGroup,
  fetchClasses,
  fetchGroups,
  updateGroup,
} from "../../../teacherStore";
import useWaitApiCall from "../../../hooks/useWaitApiCall";
import Button from "../../../atoms/Button";
import GroupClassList from "../GroupClassList";
import { IGroup } from "../../../state";
import ModalEditGroup from "../../../molecules/teacher/ModalEditGroup";
import GroupSettingClassDropdown from "../../../atoms/GroupSettingClassDropdown";

import styles from "./ModalEditGroupClass.module.scss";

export type Props = {
  classIDs: number[];
  onClose: () => void;
};

const MAX_GROUP_COUNT = 40;

const ModalEditGroupClass: React.FC<Props> = ({ classIDs, onClose }) => {
  const { state, dispatch } = React.useContext(Context);
  const [_calling, peep] = useWaitApiCall(state);

  const [groupCount, setGroupCount] = React.useState("");
  const [openModalEditGroup, setOpenModalEditGroup] = React.useState<{
    group: IGroup | null;
    state: boolean;
  }>(() => ({
    group: null,
    state: false,
  }));
  const [selectedGroupDelete, setSelectedGroupDelete] = React.useState<
    number | null
  >(null);

  const [selectedClass, setSelectedClass] = React.useState<number>(0);

  const classIDsStr = classIDs.join(",");
  const clsIDs = React.useMemo(
    () => classIDsStr.split(",").map(s => Number(s)),
    [classIDsStr],
  );
  const classes = React.useMemo(
    () => state.classes.filter(cls => clsIDs.includes(cls.id)),
    [clsIDs, state.classes],
  );
  const options = React.useMemo(() => {
    return classes.map(klass => ({
      value: klass.id,
      label: klass.full_name,
    }));
  }, [classes]);

  const inputRef = useRef<HTMLInputElement>(null);

  const isExceedingGroupLimit =
    MAX_GROUP_COUNT <
    parseInt(groupCount) +
      state.groups.filter(group => group.klass.id === selectedClass).length;

  React.useEffect(() => {
    !!options.length && setSelectedClass(options[0].value);
  }, [options]);

  React.useEffect(() => {
    if (state.success_message) {
      setOpenModalEditGroup({
        group: null,
        state: false,
      });
    }
  }, [state.success_message]);

  React.useEffect(() => {
    if (state.success_message === BULK_CREATE_GROUPS_SUCCESS_MESSAGE) {
      dispatch(fetchGroups(clsIDs));
      dispatch(clearSuccessMessage());
    }
  }, [dispatch, state.success_message, clsIDs]);

  React.useEffect(() => {
    dispatch(fetchGroups(clsIDs));
    dispatch(fetchClasses());
  }, [clsIDs, dispatch]);

  const onGroupCreationSubmit = React.useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (isExceedingGroupLimit) {
        inputRef.current?.setCustomValidity(
          `作成可能なグループ数の上限は${MAX_GROUP_COUNT}です`,
        );
      }

      if (!isExceedingGroupLimit && selectedClass) {
        dispatch(peep(bulkCreateGroups(selectedClass, parseInt(groupCount))));
        setGroupCount("");
      }

      inputRef.current?.reportValidity();
    },
    [dispatch, peep, groupCount, selectedClass, isExceedingGroupLimit],
  );

  const updateGroupFunc = React.useCallback(
    (group: IGroup) => {
      dispatch(peep(updateGroup(group)));
    },
    [dispatch, peep],
  );

  const deleteGroupFunc = React.useCallback(
    (groupId: number) => dispatch(peep(deleteGroup(groupId))),
    [dispatch, peep],
  );

  const updateGroupName = (group: IGroup) => {
    setOpenModalEditGroup({ group, state: true });
  };

  const deleteGroupName = (id: number) => {
    setSelectedGroupDelete(id);
  };

  return (
    <Modal
      centered
      className={clsx(
        styles.modal,
        state.groups.length === 0 && [
          styles.newGroupModal,
          classIDs.length > 1 && styles.newGroupModalWithMultiClass,
        ],
      )}
      onClose={onClose}
    >
      {openModalEditGroup.state && (
        <ModalEditGroup
          group={openModalEditGroup.group}
          onSave={updateGroupFunc}
          onClose={() => setOpenModalEditGroup({ state: false, group: null })}
        />
      )}
      {selectedGroupDelete && (
        <Modal
          onClose={() => setSelectedGroupDelete(null)}
          className={styles.modalNotify}
        >
          <div className={styles.modalNotify__title}>
            グループを本当に削除しますか？
          </div>
          <div className={styles.modalNotify__actions}>
            <button
              className={styles.btnCancel}
              onClick={() => setSelectedGroupDelete(null)}
            >
              キャンセル
            </button>
            <button
              className={styles.btnDelete}
              onClick={() => {
                deleteGroupFunc(selectedGroupDelete);
                setSelectedGroupDelete(null);
              }}
            >
              削除
            </button>
          </div>
        </Modal>
      )}
      <div className={styles.modal__closeButton} onClick={onClose} />
      <div className={styles.modal__content}>
        <p className={styles.title}>グループ設定</p>
        {state.groups.length === 0 ? (
          <p className={styles.initialDescription}>
            授業運営に必要な数のグループを作成してください。グループ名は変更可能です。
          </p>
        ) : (
          <>
            <p className={styles.description}>
              以下のグループが生徒に表示されています。
            </p>
            <GroupClassList
              groups={state.groups}
              displayClassName={clsIDs.length > 1}
              onUpdateGroupName={updateGroupName}
              onDeleteGroupName={deleteGroupName}
            />
          </>
        )}
        <form
          className={clsx(
            "d-flex flex-column justify-content-between",
            styles.groupFormContainer,
          )}
          onSubmit={onGroupCreationSubmit}
        >
          {clsIDs.length > 1 && (
            <GroupSettingClassDropdown
              options={options}
              defaultValue={options[0]}
              onChange={value => {
                setSelectedClass(value);
              }}
            />
          )}
          <div className={clsx("d-flex", styles.groupForm)}>
            <input
              ref={inputRef}
              type="text"
              pattern="^[1-9][0-9]*$"
              inputMode="numeric"
              placeholder="追加するグループの数を入力してください。（半角数字）"
              value={groupCount}
              onChange={e => {
                inputRef.current?.setCustomValidity("");
                setGroupCount(e.target.value);
              }}
              required
            />
            <Button name="グループを追加" variant="normal" />
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default ModalEditGroupClass;
